import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import returnService from '../../../services/returnService';
import AppDataGrid from '../../common/AppDataGrid';
import GridActionsCell from '../../common/GridActionsCell';
import TableSearch from '../../common/TableSearch';
import { useDataTable } from '../../../hooks/useDataTable';

const STATUS_COLORS = {
    PENDING: 'warning',
    APPROVED: 'info',
    REJECTED: 'error',
    COMPLETED: 'success',
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric', month: 'short', day: 'numeric',
    });
};

const ReturnsList = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Page, size, search, sort and the status filter live in the URL (/returns?page=0&size=20&status=PENDING).
    const table = useDataTable(returnService.getReturnsPage, {}, {
        onError: () => toast.error(t('returns.fetchError')),
    });
    const statusFilter = table.params.status || 'all';

    // typeLabel / reasonLabel / statusLabel / customerName are derived server-side, not sortable columns.
    const columns = [
        {
            field: 'returnNumber',
            headerName: t('returns.returnNumber'),
            width: 180,
            cellRenderer: (params) => (
                <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 'inherit' }}>
                    {params.value}
                </Typography>
            ),
        },
        { field: 'orderNumber', headerName: t('orders.order'), width: 190 },
        { field: 'customerName', headerName: t('customers.customer'), flex: 1, minWidth: 160, sortable: false, valueFormatter: (p) => p.value || '-' },
        { field: 'typeLabel', headerName: t('returns.type'), width: 140, sortable: false },
        { field: 'reasonLabel', headerName: t('returns.reason'), width: 180, sortable: false },
        {
            field: 'status',
            headerName: t('common.status'),
            width: 150,
            cellRenderer: (params) => (
                <Chip label={params.data.statusLabel} color={STATUS_COLORS[params.value] || 'default'} size="small" />
            ),
            tooltipValueGetter: (params) => params.data.statusLabel,
        },
        {
            field: 'createdAt',
            headerName: t('common.date'),
            width: 140,
            cellRenderer: (params) => formatDate(params.value),
            tooltipValueGetter: (params) => formatDate(params.value),
        },
        {
            field: 'actions',
            headerName: t('common.actions'),
            width: 110,
            sortable: false,
            cellRenderer: (params) => (
                <GridActionsCell
                    actions={[
                        {
                            key: 'view',
                            icon: ViewIcon,
                            title: t('common.view'),
                            color: 'info',
                            onClick: () => navigate(`/returns/${params.data.id}`),
                        },
                    ]}
                />
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">{t('returns.title')}</Typography>
            </Box>

            <Card>
                <CardContent>
                    <TableSearch
                        placeholder={t('common.search')}
                        value={table.searchInput}
                        onChange={table.handleSearchChange}
                        sx={{ mb: 2 }}
                    />

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={statusFilter}
                        onChange={(_, next) => next && table.updateParams({ status: next })}
                        sx={{ mb: 2 }}
                    >
                        <ToggleButton value="all">{t('common.filterAll')}</ToggleButton>
                        <ToggleButton value="PENDING">{t('returns.status.PENDING')}</ToggleButton>
                        <ToggleButton value="APPROVED">{t('returns.status.APPROVED')}</ToggleButton>
                        <ToggleButton value="COMPLETED">{t('returns.status.COMPLETED')}</ToggleButton>
                        <ToggleButton value="REJECTED">{t('returns.status.REJECTED')}</ToggleButton>
                    </ToggleButtonGroup>

                    <AppDataGrid
                        rows={table.data.content}
                        columns={columns}
                        loading={table.loading}
                        paginationMode="server"
                        rowCount={table.data.totalElements}
                        paginationModel={table.paginationModel}
                        onPaginationModelChange={table.handlePaginationModelChange}
                        sortModel={table.sortModel}
                        onSortModelChange={table.handleSortChange}
                        pageSizeOptions={[10, 20, 50, 100]}
                        getRowId={(row) => row.id}
                        onRowClicked={(e) => {
                            if (!e.event?.target?.closest?.('button')) navigate(`/returns/${e.data.id}`);
                        }}
                    />
                </CardContent>
            </Card>
        </Box>
    );
};

export default ReturnsList;
